import { create } from 'zustand';
import { capabilities } from '@/capabilities.ts';
import { diagLog } from '@/bridge/embeddedBridge.ts';
import { useAiStore } from '@/store/aiStore.ts';
// Cross-store read/write, used by ONE branch of setWorkspaceRoute (board arrival re-asserts the
// shot actions panel). Safe: projectStore.ts does not import uiStore.ts, directly or through any
// of its own imports, so there is no cycle — re-verify that before adding a second use here.
import { useProjectStore } from '@/store/projectStore.ts';
// Read-only, same branch: the guided tour owns the panel's open/shut state while it runs.
// Safe for the same reason — tutorialStore.ts statically imports nothing but zustand (its one
// uiStore touch is a dynamic import inside end()), so this direction cannot close a cycle.
import { useTutorialStore, selectExampleTourActive } from '@/store/tutorialStore.ts';

type QuickConfirmMode = 'enhance' | 'video' | 'transition' | null;

export type AiWorkspaceTask = 'enhance' | 'video' | 'continuous';
/** Tab order in AiWorkspace.tsx's TABS array: Create, Enhance, Touch Up, Import.
 *  'create' is the landing tab (2026-08-25) — every entry point that opens the workspace with an
 *  Enhance INTENT (a pre-selected shot, a first/last pair, a task shortcut, the tutorial hand-off)
 *  must therefore say so explicitly with setAiWorkspaceTab('stylegrade'). */
export type AiWorkspaceTab = 'create' | 'stylegrade' | 'touchup' | 'import';
export type SceneBoardViewMode = 'compact' | 'expanded';

export type WorkspaceRoute = 'screenplay' | 'board' | 'timeline';

export type ShotPanelView = 'main' | 'script';

/**
 * The Breakdown workspace's sub-tab (Script / Cast / Locations). 20260921 — moved out of
 * ScreenplayManager's own `useState` (spec docs/superpowers/specs/2026-09-21-characters-tour-
 * design.md §2.2): the Characters tour's rail has to both READ which sub-tab is showing (to pick
 * its panel — 2 vs 3 in §2.3) and DRIVE it (land the user on Cast for panel 2, and again on the
 * new-member card for the finale) from a component mounted elsewhere in the board tree, which
 * local state to ScreenplayManager can't be reached from. ScreenplayManager re-exports this as
 * `BrowseTab` so its existing callers keep compiling unchanged.
 */
export type ScreenplayBrowseTab = 'screenplay' | 'characters' | 'locations';

export type AudioWorkspaceMode = 'tts' | 'record' | 'import';
export type AudioLaneIndex = 0 | 1 | 2 | 3;

export type ScratchType = 'dialogue' | 'music' | 'sfx' | 'voiceover';
export type ScratchScope = 'allScenes' | 'selectedScene' | 'selectedShot' | 'custom';

export interface AnimaticRegenPending {
  shotId: number;
  newDuration: number;
  previousDuration: number;
}

/**
 * The prompt BoardEnhanceDialog hands to the AI Workspace when the user leaves through "Open AI
 * Studio" with text they typed themselves (owner item C, 2026-09-01).
 *
 * `target` names the BOX, not the tab: 'create' is the Create tab's shot-description field,
 * 'enhance' the Enhance tab's style prompt. They are separate pieces of hook state with separate
 * seeding rules, so the consumer has to know which one it is holding.
 */
export interface AiWorkspacePromptHandoff {
  prompt: string;
  target: 'create' | 'enhance';
}

interface UiState {
  isFullscreen: boolean;
  shortcutsOverlayOpen: boolean;
  drawingMode: boolean;
  commentPanelOpen: boolean;
  historyPanelOpen: boolean;
  scriptPanelOpen: boolean;
  /**
   * 20260920 (owner) — TRUE once the user has decided for themselves whether the script panel is
   * open, which retires the mount-time auto-open for the rest of the session.
   *
   * The panel's VALUE always survived a tab switch (this is a module-level store); what did not
   * was the user's authority over it. TopHeader unmounts with the Timeline tab and its mount
   * effect re-ran the auto-open decision on every return, so closing the panel, switching tabs and
   * coming back put it straight back up. The owner's words: "it's annoying to switch tabs and have
   * it act like a fresh run if I just closed it."
   *
   * IN-SESSION ONLY, deliberately: nothing writes this (or scriptPanelOpen) to localStorage or to
   * the .previs file, so a fresh launch still gets the shipped auto-open — the owner asked for the
   * same starting settings. Carrying the choice across launches would be a separate, deliberate
   * change, not a gap somebody forgot to fill.
   *
   * Lifecycle: cleared by a fresh open / project switch (actions.ts, beside boardSelectMode) and
   * by nothing else. In particular NOT by a scene change and NOT by a preserving repush — see
   * applyScriptPanelAutoOpen for why the scene change is the one worth arguing about.
   */
  scriptPanelDecidedByUser: boolean;
  audioLaneExpanded: boolean;
  recordingLane: number;
  isRecording: boolean;
  startRecordingFn: (() => void) | null;
  stopRecordingFn: (() => void) | null;
  reviewAutoPlay: boolean;
  pixelsPerSecond: number;
  scrollLeft: number;
  forceMobile: boolean;
  aiEnhanceDialogOpen: boolean;
  aiTtsDialogOpen: boolean;
  helpDialogOpen: boolean;
  aiHelpDialogOpen: boolean;
  glowUpDialogOpen: boolean;
  quickConfirmMode: QuickConfirmMode;
  ttsPopoverOpen: boolean;
  ttsGenerating: boolean;
  aiWorkspaceOpen: boolean;
  aiWorkspaceTask: AiWorkspaceTask;
  aiWorkspaceTab: AiWorkspaceTab;
  aiWorkspaceInitialPair: { fromShotId: number; toShotId: number } | null;
  aiWorkspacePreSelectedShotId: number | null;
  aiClosePendingAction: (() => void) | null;
  animaticRegenPending: AnimaticRegenPending | null;
  aiErrorDialog: { title: string; message: string } | null;

  // Scene-board workspace route (Board ⇄ Timeline ⇄ Screenplay tab). Lifted from
  // SceneBoardPage local state so board actions + the AI-workspace close can drive it.
  workspaceRoute: WorkspaceRoute;
  setWorkspaceRoute: (route: WorkspaceRoute) => void;
  // One-shot latch (2026-08-25): an arrival at the Board OUT OF an open AI Workspace.
  // setWorkspaceRoute's board-arrival assert below runs synchronously inside the tab click, but
  // the workspace hands its own shot back from a React UNMOUNT CLEANUP that lands after the
  // commit (AiWorkspace.tsx → closeSelectionSync), and that hand-off writes selectedShotId +
  // userInitiatedShotSelection in ONE set — clobbering the assert and deriving the panel shut on
  // the shot the user just picked. The latch carries the arrival's intent across that gap: armed
  // in the SAME set as the route (so it is self-clearing on every route write and can never leak
  // into a later, unrelated close) and consumed once, read-and-cleared, by the hand-off.
  //
  // TWO writers, both meaning "the user is arriving at the Board": the tab click above, and
  // (20260902) a returnToBoard close in closeAiWorkspace — the header X on a workspace the board
  // itself opened lands on the Board just as squarely, and leaving it cold parked a selected tile
  // beside a panel showing ProjectActionsView. A close that falls back to the TIMELINE is not an
  // arrival and never arms. Both writers share the tutorial exclusion.
  boardPanelAssertPending: boolean;
  /** Read-and-clear. True only for an arrival at the Board out of an open AI Workspace. */
  consumeBoardPanelAssert: () => boolean;
  // One-shot: set when the AI Workspace is opened from the board's Generate button,
  // so closing it returns to the Board tab instead of falling back to the Timeline.
  aiWorkspaceReturnToBoard: boolean;
  /**
   * ONE-SHOT SEED for the Create tab's "Add as new shot" checkbox (2026-08-28, owner item 3).
   *
   * `createAddAsNewShot` is `useState` INSIDE useAiWorkspaceFlow, so a caller that wants the
   * workspace to open with the box already ticked has no way to reach it — the hook mounts with
   * the route. This latch is that channel, and it is deliberately the boardPanelAssertPending
   * idiom rather than a new one: armed in the SAME set() as the route write, so every later route
   * write clears it and an arm that is never consumed can never leak into a later, unrelated open.
   *
   * The only armer is BoardEnhanceDialog's "Open AI Studio" button in DEFERRED mode, where the
   * workspace's own "Add as new shot" IS the deferred semantics the user came for.
   */
  createAddAsNewShotPending: boolean;
  /** Read-and-clear. True only for a hand-off that asked for the box to start ticked. */
  consumeCreateAddAsNewShot: () => boolean;
  /**
   * ONE-SHOT PROMPT HAND-OFF from the board dialog's "Open AI Studio" (2026-09-01, owner item C).
   *
   * Both prompt boxes are `useState` INSIDE useAiWorkspaceFlow, and the dialog and the workspace
   * mount two SEPARATE instances of that hook — so a user who typed into the dialog and then left
   * for the full workspace used to arrive at an empty box. This latch is the channel, and it is
   * deliberately the createAddAsNewShotPending idiom rather than a new one: armed in the SAME set()
   * as the route write, so every later route write clears it and an arm that is never consumed can
   * never leak into a later, unrelated open.
   *
   * The only armer is BoardEnhanceDialog, and only when the user actually EDITED the box — a
   * dialog-local dirty flag, because the seeded preset boilerplate is not theirs and must not
   * follow them.
   */
  aiWorkspacePromptHandoff: AiWorkspacePromptHandoff | null;
  /** Read-and-clear. Non-null only for a hand-off carrying prompt text the user typed. */
  consumeAiWorkspacePromptHandoff: () => AiWorkspacePromptHandoff | null;
  /**
   * `shotId` optional since 2026-08-31: the no-selection panel's AI Studio row opens the
   * workspace UNSCOPED (owner ruling) — `openAiWorkspace`'s preSelectedShotId was always
   * optional; that caller passes `tab: 'create'` (Enhance needs a shot, Create does not).
   * Since 2026-09-01 (T1+T2) that landing really does select nothing: `useAiWorkspaceFlow`'s
   * open effect gates its playhead seed on the landing tab, so a 'create' landing with no
   * `shotId` no longer silently highlights the first shot. It reads `aiWorkspaceReturnToBoard`
   * — set true just below, so every arrival through THIS action is recognisable — to tell the
   * two unscoped surfaces apart: a BOARD-originated open selects NOTHING (the panel is showing
   * ProjectActionsView, i.e. "nothing selected", even though `projectStore.selectedShotId` is
   * usually non-null from a programmatic select), while the Timeline gutter mirrors
   * `projectStore.selectedShotId` because there it is the visible ShotLane highlight.
   *
   * `tab` names the landing tab and DEFAULTS to 'stylegrade' — the value this action force-set
   * unconditionally until 2026-08-25, so every existing caller keeps its behaviour by omitting it.
   * BoardEnhanceDialog's "Open AI Studio" footer button is the one caller that passes it: the
   * Create arm exits to the Create tab rather than to Enhance, and DEFERRED mode names 'create'
   * outright (there is no arm to follow there — see the dialog's onOpenAiStudio).
   *
   * `opts.createAddAsNewShot` arms createAddAsNewShotPending above, and defaults to false so every
   * existing caller keeps its behaviour by omitting the whole argument. `opts.promptHandoff` arms
   * aiWorkspacePromptHandoff on the same terms.
   */
  enterAiWorkspaceFromBoard: (
    shotId?: number,
    tab?: AiWorkspaceTab,
    opts?: { createAddAsNewShot?: boolean; promptHandoff?: AiWorkspacePromptHandoff },
  ) => void;

  // Board Enhance dialog (2026-08-21): the shot whose Enhance chip/row was pressed, or null.
  // The in-place replacement for the board→Timeline hop above — AI Studio still hops, Enhance
  // does not. Deliberately a SEPARATE field from aiWorkspaceOpen: the two surfaces host the
  // same flow hook and must never be armed at once (see useAiWorkspaceFlow's mirror comment).
  boardEnhanceShotId: number | null;
  /**
   * DEFERRED MODE (2026-08-28, owner item 3). Non-null means "this run has NO target shot yet":
   * `boardEnhanceShotId` is the scene's LAST shot, borrowed as an ANCHOR for the flow's scope,
   * aspect and cost estimate ONLY, and Apply creates a brand-new shot in THIS scene before it
   * writes a pixel. Nothing in that mode may write to the anchor — painting it is exactly the
   * failure the deferred flow exists to remove (the user asked for a NEW shot, not a repaint of
   * the one that happens to be last).
   *
   * Set only by the Add-shot chooser's "Sketch with AI" row. Every other Enhance entry point (the
   * tile's AI chip, the panel's row) opens with it null and keeps the shipped write-to-this-shot
   * behaviour.
   */
  boardEnhanceDeferredSceneId: number | null;
  /**
   * ENTRY CONTEXT (2026-08-31): which arm BoardEnhanceDialog opens on. The tile's AI chip passes
   * 'enhance'; every other entry point (the panel row, the Add-shot chooser) omits the argument
   * and keeps the shipped Create-first open.
   */
  boardEnhanceInitialArm: 'create' | 'enhance';
  openBoardEnhanceDialog: (shotId: number, deferredSceneId?: number | null, initialArm?: 'create' | 'enhance') => void;
  closeBoardEnhanceDialog: () => void;

  /**
   * The Add-shot CHOOSER (2026-08-28, owner item 3) — the two-row dialog ("Sketch with AI" /
   * "Build in 3D") the board's Add-shot tile now opens instead of creating a shot outright.
   * Holds the scene the tile belongs to, or null when it is shut.
   *
   * Store-backed for the same reason boardEnhanceShotId is: SceneBoardPage's Escape resolver
   * cannot see board-local state, so a hardware Escape under a `useState` chooser would descend a
   * rung and deselect the shot behind it (that residual is documented at the resolver).
   */
  addShotChooserSceneId: number | null;
  /**
   * 20261008 (owner) — `opts.afterShotId` opens the chooser in INSERT mode: the shot "⋯" menu's
   * Insert row, whose three doors each create the new shot right AFTER that shot instead of at the
   * end of the scene. Omitted (every Add-shot tile / scene-menu / drop caller) = plain add mode.
   */
  openAddShotChooser: (sceneId: number, opts?: { afterShotId?: number | null }) => void;
  /**
   * INSERT MODE (20261008, owner). Non-null = the chooser was opened from a shot's "⋯" → Insert
   * and every door creates its shot directly after THIS shot (`addShotToScene { sceneId,
   * afterShotId }`). Additive exactly like `addShotChooserReplaceShotId`: `addShotChooserSceneId`
   * is still set (to the anchor's scene), so every Escape rung, backstop and `boardOverlayOpen`
   * member that keys off it covers insert mode for free. Null in add and replace mode and when
   * the chooser is shut; every open writes it and `closeAddShotChooser` clears it.
   */
  addShotChooserAfterShotId: number | null;
  /**
   * REPLACE MODE (20260925, the shot "⋯" menu's Replace row). The same chooser, opened on an
   * EXISTING shot: its doors rebuild that shot instead of adding one. Additive on purpose —
   * `addShotChooserSceneId` is still set (to the shot's scene) in this mode, so every Escape
   * rung, backstop, leave-reset and `boardOverlayOpen` member that keys off it covers replace
   * mode for free. Null in add mode and when the chooser is shut. `openAddShotChooser` clears it
   * (an add-mode open never inherits a stale target); `closeAddShotChooser` clears both fields.
   */
  addShotChooserReplaceShotId: number | null;
  openReplaceShotChooser: (sceneId: number, shotId: number) => void;
  closeAddShotChooser: () => void;

  /**
   * NATIVE IMAGE PICK (20260925, the chooser's "Import an image" row). True while
   * bridge/features/imagePick.ts is waiting for Unity's `shotImagePicked` answer — the board shows
   * a light scrim + "Choose an image…" for the whole wait, because the macOS image panel is
   * non-modal and the board behind it would otherwise stay clickable. Written ONLY by imagePick.ts.
   */
  imagePickPending: boolean;
  setImagePickPending: (pending: boolean) => void;
  /**
   * The web's "photos access denied" dialog, raised by imagePick.ts on a `photos-denied` answer.
   * The web draws it because Unity's own popup would sit UNDER the board WebView.
   */
  photosDeniedDialogOpen: boolean;
  setPhotosDeniedDialogOpen: (open: boolean) => void;

  // Audio workspace
  audioWorkspaceOpen: boolean;
  audioWorkspaceMode: AudioWorkspaceMode;
  audioWorkspaceTargetLane: AudioLaneIndex;
  scratchType: ScratchType;
  scratchScope: ScratchScope;
  scratchScriptCoverage: boolean;
  setScratchType: (type: ScratchType) => void;
  setScratchScope: (scope: ScratchScope) => void;
  setScratchScriptCoverage: (enabled: boolean) => void;

  // Screenplay-blocking feature flag (compile-time in Unity, pushed via setFeatureFlags)
  screenplayBlocking: boolean;
  setScreenplayBlocking: (enabled: boolean) => void;

  // Scene/Shot Settings web modals feature flag (default on; Unity can push false via setFeatureFlags)
  sceneShotSettings: boolean;
  setSceneShotSettings: (enabled: boolean) => void;

  // Doodle management dialog
  doodleDialogOpen: boolean;
  doodleDialogShotId: number | null;

  // Post-draw comment prompt
  postDrawPromptOpen: boolean;
  postDrawPromptShotId: number | null;
  postDrawPromptPaths: Array<{ d: string; stroke: string; strokeWidth: number }> | null;
  postDrawPromptScreenplayPage: number | null;
  postDrawPromptSceneId: number | null;

  // Dialogs lifted from Transport local state (needed for programmatic open from DebugPage)
  exportHubOpen: boolean;
  exportHubInitialTab: 'download' | 'publish' | 'nle' | undefined;
  shareDialogOpen: boolean;

  // Script annotation mode (screenplay page markup)
  scriptAnnotationMode: boolean;
  scriptAnnotationPage: number | null;

  setFullscreen: (fs: boolean) => void;
  toggleFullscreen: () => void;
  toggleShortcutsOverlay: () => void;
  setDrawingMode: (on: boolean) => void;
  toggleDrawingMode: () => void;
  toggleCommentPanel: () => void;
  setCommentPanelOpen: (open: boolean) => void;
  toggleHistoryPanel: () => void;
  /** The user deciding. Sets scriptPanelDecidedByUser — see its declaration. */
  toggleScriptPanel: () => void;
  /** The user deciding, with an explicit value. Also sets scriptPanelDecidedByUser. */
  setScriptPanelOpen: (open: boolean) => void;
  /** The auto-open SUGGESTION, and the only write that is not the user deciding. Silent once
   *  scriptPanelDecidedByUser is set; never sets it. TopHeader's mount effect is the one caller. */
  applyScriptPanelAutoOpen: (open: boolean) => void;
  toggleAudioLane: () => void;
  setRecordingLane: (lane: number) => void;
  setStartRecordingFn: (fn: (() => void) | null) => void;
  setIsRecording: (on: boolean, stopFn?: (() => void) | null) => void;
  setReviewAutoPlay: (on: boolean) => void;
  setZoom: (pps: number) => void;
  setScrollLeft: (sl: number) => void;
  toggleForceMobile: () => void;
  openAiEnhanceDialog: () => void;
  closeAiEnhanceDialog: () => void;
  openAiTtsDialog: () => void;
  closeAiTtsDialog: () => void;
  openHelpDialog: () => void;
  closeHelpDialog: () => void;
  openAiHelpDialog: () => void;
  closeAiHelpDialog: () => void;
  openGlowUpDialog: () => void;
  closeGlowUpDialog: () => void;
  setQuickConfirmMode: (mode: QuickConfirmMode) => void;
  clearQuickConfirmMode: () => void;
  setTtsPopoverOpen: (open: boolean) => void;
  setTtsGenerating: (on: boolean) => void;
  openAiWorkspace: (task?: AiWorkspaceTask, pair?: { fromShotId: number; toShotId: number }, preSelectedShotId?: number) => void;
  closeAiWorkspace: () => void;
  confirmAiWorkspaceClose: () => void;
  cancelAiWorkspaceClose: () => void;
  setAiWorkspaceTask: (task: AiWorkspaceTask) => void;
  setAiWorkspaceTab: (tab: AiWorkspaceTab) => void;
  setAnimaticRegenPending: (pending: AnimaticRegenPending | null) => void;
  clearAnimaticRegenPending: () => void;
  showAiError: (title: string, message: string) => void;
  closeAiError: () => void;

  openAudioWorkspace: (mode?: AudioWorkspaceMode) => void;
  closeAudioWorkspace: () => void;
  setAudioWorkspaceMode: (mode: AudioWorkspaceMode) => void;
  setAudioWorkspaceTargetLane: (lane: AudioLaneIndex) => void;

  // Shared "back" predicate: close whichever workspace is showing, and report whether one
  // was. The single source of truth for every back affordance — today that is Unity's hardware
  // back/Escape (embeddedBridge's __requestBack) and the hamburger's Close Project; the tab
  // shell's top-bar arrow was a third caller until it was removed on 2026-08-09. Kept shared so
  // routes cannot drift. Callers keep their own fall-through for the "nothing was open" case.
  closeOpenWorkspace: () => boolean;

  openDoodleDialog: (shotId: number) => void;
  closeDoodleDialog: () => void;

  openPostDrawPrompt: (shotId: number, paths: Array<{ d: string; stroke: string; strokeWidth: number }>, screenplayPage?: number | null, sceneId?: number | null) => void;
  closePostDrawPrompt: () => void;

  setExportHubOpen: (open: boolean) => void;
  openExportHub: (initialTab?: 'download' | 'publish' | 'nle') => void;
  setShareDialogOpen: (open: boolean) => void;

  setScriptAnnotationMode: (on: boolean) => void;
  setScriptAnnotationPage: (page: number | null) => void;
  toggleScriptAnnotationMode: () => void;

  // Tease overlay — shown when review-mode user taps AI/Audio gutter buttons
  teaseOverlay: 'ai' | 'audio' | null;
  setTeaseOverlay: (kind: 'ai' | 'audio' | null) => void;

  // Quick Orient — first-run coach marks
  orientStep: number | null;
  setOrientStep: (step: number | null) => void;

  // Script tab's coach marks (ScreenplayManager/ScriptOrient.tsx) — same shape as orientStep, a
  // SEPARATE field so the Timeline's marks and the Script tab's can never read each other's step.
  scriptOrientStep: number | null;
  setScriptOrientStep: (step: number | null) => void;

  // 20260922 — `pendingTutorialAutoplay` LIVED HERE and is gone. It was a one-shot set by
  // TutorialRail's "Play your scene" when Quick Orient was about to auto-arm, so playback did not
  // start underneath the coach marks; TimelineView consumed it when the last mark was dismissed and
  // setWorkspaceRoute cleared it on the way off the Timeline (an incomplete clear — closeAiWorkspace's
  // returnToBoard finalize bypassed it — which was documented rather than fixed). The owner's ruling
  // that the first-entry coach marks never arm inside a tutorial project removed the race outright:
  // "Play your scene" only exists on the tutorial project's finale, so the Timeline it routes to can
  // no longer have marks on it, and playScene starts playback itself.

  // Glow tutorial — guided walk-through after Polish & Share dialog
  glowTutorialStep: 'gutter' | 'enhance' | null;
  setGlowTutorialStep: (step: 'gutter' | 'enhance' | null) => void;

  // Remote comment notification — incremented when a remote comment arrives
  remoteCommentPing: number;
  pingRemoteComment: () => void;

  // UI scaling level (0-6, maps to ExtraSmall..ExtraExtraExtraLarge; 3=Large=current default)
  uiScaleLevel: number;
  setUIScaleLevel: (level: number) => void;

  // Scene-board view mode (Compact scene list ⇄ Expanded per-scene shots) — Board route only
  sceneBoardViewMode: SceneBoardViewMode;
  setSceneBoardViewMode: (mode: SceneBoardViewMode) => void;
  toggleSceneBoardViewMode: () => void;

  // Scene-board scroll anchor + per-view saved offsets (in-session only; not persisted)
  sceneBoardAnchorSceneId: number | null;
  setSceneBoardAnchor: (sceneId: number | null) => void;
  compactScroll: { top: number; anchor: number | null } | null;
  setCompactScroll: (v: { top: number; anchor: number | null } | null) => void;
  expandedScroll: { top: number; anchor: number | null } | null;
  setExpandedScroll: (v: { top: number; anchor: number | null } | null) => void;

  // Set true the first time SceneBoardExpanded mounts this session (in-session only; not
  // persisted). Lets that component tell "a Board<->Timeline tab-switch remount" apart from
  // "the board's true first mount" so a selection made on the Timeline tab while unmounted can
  // be revealed on return, without fighting useSceneBoardScrollSync's first-mount restore.
  sceneBoardHasMounted: boolean;

  // Timeline anchor — scene the Timeline last left at unmount (Board ⇄ Timeline sync; not persisted)
  timelineAnchor: number | null;
  setTimelineAnchor: (sceneId: number | null) => void;

  // Expanded board: which scene is expanded (exclusive; null = all collapsed)
  expandedSceneId: number | null;
  setExpandedScene: (id: number | null) => void;
  toggleExpandedScene: (id: number) => void;

  /**
   * SELECT MODE on the Board (20260909, owner — spec item 2). A multi-select mode over the shot
   * tiles: while it is on a tile tap toggles MEMBERSHIP instead of selecting, and the selection
   * panel (or, on phones, a bottom bar) offers Copy / Duplicate / Delete over the whole set.
   *
   * It lives HERE rather than in SceneBoardExpanded for the same reason expandedSceneId does: the
   * SHELL has to see it. SceneBoardPage's top bar owns the Select/Cancel button, dims the rest of
   * the bar off `boardSelectMode`, and gives Escape a rung that exits — none of which can read
   * board-local state. Two writers of the ids (the tile tap and the prune) and one of the mode.
   *
   * DUMB SHELL: this is pure PRESENTATION state. Nothing here creates, deletes or reorders a shot —
   * the three actions send verbs and Unity answers with a repush, exactly as the single-shot
   * context menu already does. `boardSelectedShotIds` is a list of ids the user has ticked, in
   * BOARD ORDER at send time (the sender sorts; this array is tap order, which is not the same
   * thing and must not be relied on downstream).
   *
   * Lifecycle: exited by setWorkspaceRoute for any route but 'board' (below), by a fresh project
   * load (actions.ts, beside the expandedSceneId reset), by the shell's Cancel / the panel's
   * Cancel / Escape, and by each of the three actions the moment it fires. A repush that removes
   * shots prunes the ids (pruneBoardSelection, called from the board).
   */
  boardSelectMode: boolean;
  boardSelectedShotIds: number[];
  /** Enter select mode, seeding the set with `seedShotId` when one is given — owner ruling: the
   *  shot the user already had selected is the obvious first member. */
  enterBoardSelectMode: (seedShotId?: number) => void;
  /** Leave select mode AND drop the set: a mode that remembered its ticks would put them back on
   *  the next entry, which is not what "Cancel" means anywhere else in the app. */
  exitBoardSelectMode: () => void;
  toggleBoardShotSelected: (id: number) => void;
  /** Drop ids that are no longer on the board (a repush deleted them elsewhere, or our own batch
   *  delete landed). A no-op write is skipped so this can be called from a render effect. */
  pruneBoardSelection: (existingIds: Set<number>) => void;

  // Shot actions panel sub-view (2026-08-18). In the STORE, not the component, because
  // SceneBoardPage's route ternary unmounts the whole board subtree on a tab switch —
  // the selection survives (projectStore + setWorkspaceRoute's board-arrival re-assert)
  // and an open Script submenu must too. Same precedent as expandedSceneId above. The
  // RESET is owned by SceneBoardExpanded's not-visible→visible panel edge (see its
  // effect); nothing here resets it, and projectStore must never import this store
  // (uiStore already imports projectStore — the reverse import closes a cycle).
  shotPanelView: ShotPanelView;
  setShotPanelView: (v: ShotPanelView) => void;

  // Script-tab (ScreenplayManager) browse selection (2026-08-19). In the STORE, not BrowseMode
  // state, for the same reason as shotPanelView above: entering View Changes unmounts BrowseMode
  // (ScreenplayManager's mode ternary) and a main-tab switch unmounts the whole tab (SceneBoardPage's
  // route ternary), so component state dies exactly when the user expects their place to be kept.
  // NOT the board's expandedSceneId above — that is the BOARD's disclosure; this is the Script tab's
  // own expanded card. Never merge or shadow the two. Per-project ids/keys, so loadProject
  // (actions.ts) clears the slice on a project switch and validates the two scene-id fields against
  // the incoming scene set on a preserving repush, exactly as it does for expandedSceneId.
  // In-session only; never persisted.
  screenplayExpandedSceneId: number | null;
  /** UPPERCASED cast display name (the identity every name-keyed piece of cast UI uses). */
  screenplayExpandedCharacter: string | null;
  /** LocationGroup.key: `id:<rowId>` / `name:<UPPER>` / 'unassigned'. */
  screenplayExpandedLocation: string | null;
  screenplayFilterSceneId: number | null;
  screenplaySearchQuery: string;
  /** Which of Script / Cast / Locations is showing — see ScreenplayBrowseTab above for why this
   *  moved here. NOT part of loadProject's screenplay-slice reset below (actions.ts), deliberately:
   *  that reset clears project-SCOPED selection (an expanded card, a filter, a search string), but
   *  this is a navigation position with no project identity of its own, and the local-`useState` it
   *  replaces never reset on a non-remounting project swap either — only an actual ScreenplayManager
   *  remount did, which is exactly what its own unmount cleanup (see ScreenplayManager.tsx) still
   *  does. Folding it into the loadProject reset would be a user-visible behavior change this
   *  migration is not supposed to make.
   */
  screenplayBrowseTab: ScreenplayBrowseTab;
  /**
   * ONE-SHOT "bring this cast card into view" (20260921, design §2.5). Armed by the bridge's
   * `castMemberAdded` handler — the character the creator just baked is expanded, and on a long
   * roster it is expanded somewhere off-screen — and consumed by BrowseMode the first render the
   * card actually EXISTS.
   *
   * One-shot, and separate from `screenplayExpandedCharacter`, for two reasons:
   *   · expanding a card is something the user does constantly by tapping it, and a scroll on
   *     every one of those taps would fight the list out from under them. Only this arrival
   *     scrolls;
   *   · the repush that carries the new cast row is ASYNC (Unity reloads the file after the bake),
   *     so the message routinely lands BEFORE the roster does. A latch waits; a one-off scroll
   *     attempt at message time would find no card and silently do nothing.
   * Holds the same UPPERCASED key `screenplayExpandedCharacter` does.
   */
  screenplayRevealCharacter: string | null;
  setScreenplayRevealCharacter: (name: string | null) => void;
  /** Read-and-CLEAR (the consumeCoverageOpenRequest idiom): the reveal happens once, so a second
   *  consumer — a StrictMode double-run, a re-render on the next repush — must find nothing. */
  consumeScreenplayRevealCharacter: () => string | null;
  setScreenplayExpandedSceneId: (id: number | null) => void;
  setScreenplayExpandedCharacter: (name: string | null) => void;
  setScreenplayExpandedLocation: (key: string | null) => void;
  setScreenplayFilterSceneId: (id: number | null) => void;
  setScreenplaySearchQuery: (query: string) => void;
  setScreenplayBrowseTab: (tab: ScreenplayBrowseTab) => void;
  // Expansion is exclusive per tab (at most one card open), same contract as toggleExpandedScene.
  toggleScreenplayExpandedScene: (id: number) => void;
  toggleScreenplayExpandedCharacter: (name: string) => void;
  toggleScreenplayExpandedLocation: (key: string) => void;

  // Canvas-entry gate (2026-08-18): true from an Edit-in-3D press until Unity's
  // canvasEntryFinished (or the 45s watchdog in canvasEntryBusy.ts). PURE PRESENTATION
  // state (dumb-shell rule): it creates/mutates nothing — Unity owns the canvas
  // transition and both the arm window and the clear signal; the flag only drives the
  // full-screen "Loading scene…" scrim SceneBoardPage renders over both board modes.
  canvasEntryBusy: boolean;
  setCanvasEntryBusy: (busy: boolean) => void;

  // Scene-mutation scrim (2026-08-28, owner item 8): the op whose "Adding / Inserting /
  // Duplicating scene…" scrim is up, armed by sceneMutateBusy.beginSceneMutate on the PRESS — the
  // Add-scene row's, and the scene context menu's Insert and Duplicate — and cleared by the
  // runner's finally (or
  // that module's watchdog). PURE PRESENTATION state, exactly like canvasEntryBusy above: it
  // creates and mutates nothing — Unity owns the create, and SceneBoardExpanded keeps its own
  // local `mutatingScene` as the double-tap guard and as a boardOverlayOpen term.
  //
  // It lives HERE rather than in SceneBoardExpanded for one reason, and it is a latency reason:
  // SceneBoardExpanded renders every scene section and every shot tile, so arming a flag inside
  // it costs a full board re-render before the browser can paint the scrim, which is exactly the
  // "the spinner shows up too late" the owner reported. Only SceneMutateBusyOverlay subscribes to
  // this field, and SceneBoardPage renders that leaf unconditionally — so arming it re-renders one
  // tiny component and the scrim reaches the screen in one cheap commit. Nothing else may
  // subscribe to it: a page-level `useUiStore((s) => s.sceneMutationBusy)` would re-render
  // SceneBoardPage, which renders SceneBoardExpanded as a child, and the whole fix would be undone
  // (that is the trap canvasEntryBusy's page-level subscription falls into).
  sceneMutationBusy: 'add' | 'insert' | 'duplicate' | null;
  setSceneMutationBusy: (op: 'add' | 'insert' | 'duplicate' | null) => void;

  // Scene-board top bar: true while the shell has shed its Home button and its "?" (they go
  // together — see SceneBoard/shellBarFit.ts). PURE PRESENTATION state, published by SceneBoardPage
  // from a MEASUREMENT of the bar; nothing writes it from outside that component and nothing acts
  // on it but Quick Orient.
  //
  // It lives here because the one reader is TimelineView, a sibling subtree: its fifth coach-mark
  // step points at the Home button when that button is on screen and at the hamburger's Close
  // Project item when it is not (buildBackToProjectsStep in hooks/useQuickOrient.ts). That used to
  // be a matchMedia read against a `(max-width: 600px)` constant kept in step with a CSS
  // breakpoint by hand — a three-way contract that went stale the moment the breakpoints became a
  // measurement. Default false: on every route with no scene-board shell (bare embedded Timeline,
  // standalone web) the step keeps its Home-button target, exactly as the old matchMedia default
  // did in jsdom.
  shellHomeShed: boolean;
  setShellHomeShed: (shed: boolean) => void;

  /**
   * ONE-SHOT NAVIGATION LATCHES for the SCRIPT IMPORT CHECKLIST card (owner, 20260914 — spec
   * docs/superpowers/specs/2026-09-14-script-import-checklist-design.md §2.2).
   *
   * Deliberately the createAddAsNewShotPending idiom above rather than a new one: a checklist row
   * has to do TWO things — put the user on the right tab, and then make that tab do something (open
   * the Coverage overview on a scene, enter View Changes) — and the component that owns the second
   * half has not mounted yet when the row is tapped (SceneBoardPage's route ternary unmounts the
   * other tab's whole subtree). The latch is that channel.
   *
   * SELF-CLEARING: both ride setWorkspaceRoute's trailing set(), so any later route write drops an
   * arm nobody consumed rather than letting it fire on some later, unrelated tab entry. Which is
   * exactly why the two request actions write the ROUTE FIRST and the LATCH SECOND, in two set()s
   * and never merged into one — an arm written in the same breath as the route would be cleared by
   * the very write that was supposed to carry it.
   */
  coverageOpenRequestSceneId: number | null;
  /** Read-and-clear. The scene whose Coverage overview the board should open on arrival, or null. */
  consumeCoverageOpenRequest: () => number | null;
  /** Board tab, expanded mode, `sceneId` disclosed and anchored, its Coverage overview asked for.
   *  SceneBoardExpanded consumes that last part. */
  requestCoverageForScene: (sceneId: number) => void;
  scriptViewChangesRequestPending: boolean;
  /** Read-and-clear. True only for a hand-off that asked the Script tab to enter View Changes. */
  consumeScriptViewChangesRequest: () => boolean;
  /** Script tab, then View Changes. ScreenplayManager (tab host only) consumes the second half. */
  requestScriptViewChanges: () => void;
}

export const useUiStore = create<UiState>((set, get) => ({
  isFullscreen: false,
  shortcutsOverlayOpen: false,
  drawingMode: false,
  commentPanelOpen: false,
  historyPanelOpen: false,
  scriptPanelOpen: false,
  scriptPanelDecidedByUser: false,
  audioLaneExpanded: false,
  recordingLane: 0,
  isRecording: false,
  startRecordingFn: null,
  stopRecordingFn: null,
  reviewAutoPlay: false,
  pixelsPerSecond: 80,
  scrollLeft: 0,
  forceMobile: false,
  aiEnhanceDialogOpen: false,
  aiTtsDialogOpen: false,
  helpDialogOpen: false,
  aiHelpDialogOpen: false,
  glowUpDialogOpen: false,
  quickConfirmMode: null,
  ttsPopoverOpen: false,
  ttsGenerating: false,
  aiWorkspaceOpen: false,
  aiWorkspaceTask: 'enhance',
  aiWorkspaceTab: 'create',
  aiWorkspaceInitialPair: null,
  aiWorkspacePreSelectedShotId: null,
  aiClosePendingAction: null,
  animaticRegenPending: null,
  aiErrorDialog: null,
  workspaceRoute: 'board',
  boardPanelAssertPending: false,
  createAddAsNewShotPending: false,
  aiWorkspacePromptHandoff: null,
  aiWorkspaceReturnToBoard: false,
  boardEnhanceShotId: null,
  boardEnhanceDeferredSceneId: null,
  boardEnhanceInitialArm: 'create',
  addShotChooserSceneId: null,
  addShotChooserAfterShotId: null,
  addShotChooserReplaceShotId: null,
  imagePickPending: false,
  photosDeniedDialogOpen: false,
  audioWorkspaceOpen: false,
  audioWorkspaceMode: 'tts',
  audioWorkspaceTargetLane: 0,
  scratchType: 'dialogue' as ScratchType,
  scratchScope: 'allScenes' as ScratchScope,
  scratchScriptCoverage: false,
  screenplayBlocking: false,
  sceneShotSettings: true,
  doodleDialogOpen: false,
  doodleDialogShotId: null,
  postDrawPromptOpen: false,
  postDrawPromptShotId: null,
  postDrawPromptPaths: null,
  postDrawPromptScreenplayPage: null,
  postDrawPromptSceneId: null,
  exportHubOpen: false,
  exportHubInitialTab: undefined,
  shareDialogOpen: false,
  scriptAnnotationMode: false,
  scriptAnnotationPage: null,

  setFullscreen: (fs) => set({ isFullscreen: fs }),
  toggleFullscreen: () => {
    const current = useUiStore.getState().isFullscreen;
    diagLog('Fullscreen', `toggleFullscreen called — current=${current}, bridge=${capabilities.bridge}`);
    if (capabilities.bridge) {
      // Embedded mode: browser Fullscreen API is blocked by WKWebView.
      // Toggle state directly; CSS handles the visual fullscreen.
      set({ isFullscreen: !current });
      diagLog('Fullscreen', `State set to isFullscreen=${!current}`);
    } else {
      const wrapper = document.querySelector('[data-viewer-wrapper]') as HTMLElement | null;
      if (!wrapper) return;
      if (!current) {
        const rfs = wrapper.requestFullscreen
          ?? (wrapper as unknown as { webkitRequestFullscreen?: () => Promise<void> }).webkitRequestFullscreen;
        rfs?.call(wrapper);
      } else {
        const exitFs = document.exitFullscreen
          ?? (document as unknown as { webkitExitFullscreen?: () => Promise<void> }).webkitExitFullscreen;
        exitFs?.call(document);
      }
    }
  },
  toggleShortcutsOverlay: () => set(s => ({ shortcutsOverlayOpen: !s.shortcutsOverlayOpen })),
  setDrawingMode: (on) => set({ drawingMode: on }),
  toggleDrawingMode: () => set(s => ({ drawingMode: !s.drawingMode })),
  toggleCommentPanel: () => set(s => ({ commentPanelOpen: !s.commentPanelOpen })),
  setCommentPanelOpen: (open) => set({ commentPanelOpen: open }),
  toggleHistoryPanel: () => set(s => ({ historyPanelOpen: !s.historyPanelOpen })),
  // 20260920 — BOTH of these count as the user deciding, and setScriptPanelOpen counts ON PURPOSE
  // even though it takes an explicit value rather than a gesture: after this change its only
  // callers are user affordances, and a future caller who reaches for the obvious name lands on
  // the SAFE side of the split (the panel stops being re-decided for them, which is at worst one
  // extra click). The write that must NOT latch has its own name below so it cannot be picked by
  // accident — that distinction is the whole fix, so it is deliberately not a boolean argument
  // somebody has to remember to pass.
  toggleScriptPanel: () => set(s => ({ scriptPanelOpen: !s.scriptPanelOpen, scriptPanelDecidedByUser: true })),
  setScriptPanelOpen: (open) => set({ scriptPanelOpen: open, scriptPanelDecidedByUser: true }),
  // 20260920 — the auto-open, demoted to a suggestion. Gated HERE rather than at the call site for
  // two reasons: the next caller cannot forget the gate, and the effect that calls it needs no
  // dependency on scriptPanelDecidedByUser (which would re-run the effect the instant the flag
  // flipped, i.e. on the user's own close).
  //
  // THE GATE COVERS SCENE CHANGES TOO, and that is the judgement call (owner ruling 20260920,
  // "remember their state"): the shipped effect re-decided per scene, so clicking to a scene with
  // no screenplay shut a panel the user had just opened by hand, and clicking back re-opened one
  // they had just closed — the same "acts like a fresh run" complaint wearing a different hat. The
  // price is a hand-opened panel sitting on a scene with nothing in it, which is a coherent state
  // rather than a broken one: ScriptPanel renders "No script imported" for a project with no
  // screenplay, and an editable text area for a scene inside one that has no lines yet.
  //
  // Reverting this to an ungated set() restores the original bug exactly: the panel would reopen
  // on every return to the Timeline tab and on every scene click.
  applyScriptPanelAutoOpen: (open) => {
    if (get().scriptPanelDecidedByUser) return;
    set({ scriptPanelOpen: open });
  },
  toggleAudioLane: () => set(s => ({ audioLaneExpanded: !s.audioLaneExpanded })),
  setRecordingLane: (lane) => set({ recordingLane: lane }),
  setStartRecordingFn: (fn) => set({ startRecordingFn: fn }),
  setIsRecording: (on, stopFn) => set({ isRecording: on, stopRecordingFn: on ? (stopFn ?? null) : null }),
  setReviewAutoPlay: (on) => set({ reviewAutoPlay: on }),
  setZoom: (pps) => set({ pixelsPerSecond: Math.max(20, Math.min(300, pps)) }),
  setScrollLeft: (sl) => set({ scrollLeft: sl }),
  toggleForceMobile: () => set(s => ({ forceMobile: !s.forceMobile })),
  openAiEnhanceDialog: () => set({ aiEnhanceDialogOpen: true }),
  closeAiEnhanceDialog: () => set({ aiEnhanceDialogOpen: false }),
  openAiTtsDialog: () => set({ aiTtsDialogOpen: true }),
  closeAiTtsDialog: () => set({ aiTtsDialogOpen: false }),
  openHelpDialog: () => set({ helpDialogOpen: true }),
  closeHelpDialog: () => set({ helpDialogOpen: false }),
  openAiHelpDialog: () => set({ aiHelpDialogOpen: true }),
  closeAiHelpDialog: () => set({ aiHelpDialogOpen: false }),
  openGlowUpDialog: () => set({ glowUpDialogOpen: true }),
  closeGlowUpDialog: () => set({ glowUpDialogOpen: false }),
  setQuickConfirmMode: (mode) => set({ quickConfirmMode: mode }),
  clearQuickConfirmMode: () => set({ quickConfirmMode: null }),
  setTtsPopoverOpen: (open) => set({ ttsPopoverOpen: open, ...(!open ? { ttsGenerating: false } : {}) }),
  setTtsGenerating: (on) => set({ ttsGenerating: on }),
  openAiWorkspace: (task, pair, preSelectedShotId) => set({
    aiWorkspaceOpen: true,
    aiWorkspaceTask: task ?? 'enhance',
    aiWorkspaceInitialPair: pair ?? null,
    aiWorkspacePreSelectedShotId: preSelectedShotId ?? null,
    // Close conflicting overlays
    quickConfirmMode: null,
    ttsPopoverOpen: false,
    aiEnhanceDialogOpen: false,
    audioWorkspaceOpen: false,
  }),
  closeAiWorkspace: () => {
    // If the workspace was opened from the board's Generate button, return to the
    // Board tab on close (default is to fall back to the Timeline that mounted it).
    const returnToBoard = get().aiWorkspaceReturnToBoard;
    // 20260902 — a returnToBoard close IS an arrival at the Board, the same as a Board-tab click,
    // so it arms the same one-shot latch (consumed by closeSelectionSync's unmount hand-off). The
    // old "the X button never pops a panel" rule predates the permanent panel (20260828): what the
    // bypass really produced was a selected tile with its 3D/AI/Revert bar beside a panel showing
    // ProjectActionsView. Same tutorial exclusion as setWorkspaceRoute's boardArrival. When
    // setWorkspaceRoute is the caller its own trailing set() runs last and stays authoritative
    // (re-arms for 'board', clears for any other target).
    // 20260921 — EXAMPLE-ONLY (design §2.1). The exclusion exists because the Getting started tour
    // selects the glow target itself and needs the panel to stay shut; a characters run never
    // touches board selection, so it takes the ordinary arrival exactly as a plain project does.
    const boardArrival = returnToBoard && !selectExampleTourActive(useTutorialStore.getState());
    const finalize = {
      aiWorkspaceOpen: false,
      aiWorkspaceInitialPair: null,
      aiWorkspacePreSelectedShotId: null,
      aiWorkspaceTab: 'create' as AiWorkspaceTab,
      aiWorkspaceReturnToBoard: false,
      // Abandoning the workspace abandons any glow hand-off aimed at it — a parked flag
      // would otherwise light the Enhance Shot button gold on a later non-guided open.
      glowTutorialStep: null,
      ...(returnToBoard
        ? { workspaceRoute: 'board' as WorkspaceRoute, boardPanelAssertPending: boardArrival }
        : {}),
    };
    if (useAiStore.getState().isAnyGenerating()) {
      set({ aiClosePendingAction: () => set({ ...finalize, aiClosePendingAction: null }) });
    } else {
      set(finalize);
    }
  },
  confirmAiWorkspaceClose: () => {
    const action = get().aiClosePendingAction;
    if (action) action();
  },
  cancelAiWorkspaceClose: () => set({ aiClosePendingAction: null }),
  setWorkspaceRoute: (route) => {
    const current = get();
    if (current.workspaceRoute === 'timeline' && route !== 'timeline') {
      // Leaving the Timeline tab: close whichever workspace is open using the EXACT same
      // action the workspace header's X button calls. Same generation-in-progress deferral,
      // same return-to-board finalize, same "hide only, never cancel a running generation"
      // contract — see AiWorkspace.tsx / AudioWorkspace.tsx and the __requestBack precedent
      // in embeddedBridge.ts for why this must delegate, not inline setState. Check both
      // independently (never assume mutual exclusivity) since AI and Audio are separate
      // workspaces. Both flags come from the pre-close `current` snapshot — sound only
      // while closeAiWorkspace() never touches audioWorkspaceOpen (and vice versa); re-read
      // via get() if either close action ever starts mutating the other's flag.
      if (current.aiWorkspaceOpen) current.closeAiWorkspace();
      if (current.audioWorkspaceOpen) current.closeAudioWorkspace();
      // 20260922 — a `pendingTutorialAutoplay` clear used to sit here too; the field is gone (see
      // its note in the interface above).
    }
    // A real arrival AT the Board — the condition both the immediate assert below and the
    // deferred latch are scoped to. Computed once, off the pre-close `current` snapshot.
    // 20260921 — EXAMPLE-ONLY, the twin of closeAiWorkspace's above and for the same reason.
    const boardArrival = route === 'board' && current.workspaceRoute !== 'board'
      && !selectExampleTourActive(useTutorialStore.getState());
    // The user's explicit tab click always wins over any route side-effect the close helpers
    // above may have queued (closeAiWorkspace's aiWorkspaceReturnToBoard finalize sets
    // workspaceRoute:'board'); this final set() runs last and is authoritative — Zustand
    // batches synchronous set() calls into a single commit, so no intermediate render ever
    // shows the wrong route.
    //
    // The latch rides along in this same set so it is written on EVERY route change: an arm
    // that is never consumed is cleared by the next tab click rather than sitting armed for a
    // later, unrelated workspace close. Armed only when the workspace was actually up, because
    // that is the only case with a deferred hand-off to survive (see boardPanelAssertPending).
    //
    // "Actually up" needs BOTH flags off the pre-write snapshot: aiWorkspaceOpen alone can be a
    // PHANTOM true. A close deferred mid-generation parks the action in aiClosePendingAction and
    // leaves the flag set, and the workspace only ever MOUNTS on the timeline route
    // (TimelineView.tsx renders it), so arriving at the Board from screenplay/board with a stuck
    // flag would arm a latch no unmount cleanup is coming to consume — it would just sit there
    // until some later, unrelated workspace close misfired on it.
    const workspaceMounted = current.aiWorkspaceOpen && current.workspaceRoute === 'timeline';
    // createAddAsNewShotPending rides the same set() for the same self-clearing reason: it is only
    // ever armed by enterAiWorkspaceFromBoard's own route write, so ANY other route change means
    // the hand-off it was armed for never happened and the arm must not survive to the next mount.
    // aiWorkspacePromptHandoff is armed by the same one call and cleared on the same terms.
    // 20260909 — LEAVING THE BOARD EXITS SELECT MODE, and it rides this same set() rather than a
    // separate write for the same self-clearing reason as the two latches above: select mode is a
    // BOARD mode, its shell affordance (the top bar's Select/Cancel button) is only rendered on the
    // board route, and a set of ticked ids left armed behind a tab switch would come back with the
    // shell bar looking perfectly normal. Written on every route change, including a re-click of
    // the already-active Board tab, which is harmless: the mode cannot be on without the button
    // that turns it on being on screen.
    // 20260914 — the two CHECKLIST latches ride this same set() for the same self-clearing reason,
    // and unconditionally (not gated on `leavingBoard`): each one means "the tab I am routing to
    // should do this on arrival", so any route write that is not the one that armed it has already
    // invalidated it — including a re-click of the tab it was aimed at, where the consumer is
    // already mounted and would never see a second mount to consume on. This is also what makes
    // requestCoverageForScene / requestScriptViewChanges write the route FIRST and the latch
    // SECOND; see their declarations.
    const leavingBoard = route !== 'board';
    set({
      workspaceRoute: route,
      boardPanelAssertPending: boardArrival && workspaceMounted,
      createAddAsNewShotPending: false,
      aiWorkspacePromptHandoff: null,
      coverageOpenRequestSceneId: null,
      scriptViewChangesRequestPending: false,
      ...(leavingBoard ? { boardSelectMode: false, boardSelectedShotIds: [] } : {}),
    });
    // Arriving at the Board with a shot ALREADY selected (typically picked in the Timeline tab,
    // whose ShotBar selects non-user-initiated by design) must show that shot's actions panel —
    // the board's whole selected state, not half of it. useShotActionsPanelOpen derives the panel
    // from selectedShotId + userInitiatedShotSelection, and only the latter is missing here, so
    // this arrival asserts it. Scoped to an actual route CHANGE: re-clicking the already-active
    // Board tab must not re-open a panel the user just closed.
    //
    // Which AI-workspace returns land here: clicking the BOARD TAB while the workspace is open
    // DOES arrive through this branch and DOES assert. The close above may raw-set
    // workspaceRoute:'board', but `current` is the pre-close snapshot, so the route still reads
    // 'timeline' and the guard passes — intentional, and the same contract as every other board
    // arrival (the user asked for the board, so the board's selected state comes back whole).
    // The workspace header's X button reaches the Board without passing through here at all: it
    // calls closeAiWorkspace() directly, whose raw workspaceRoute:'board' write skips this assert.
    // That return is NOT an exception to the board-arrival contract though (20260902) — it arms
    // boardPanelAssertPending itself, so the hand-off asserts on its behalf a beat later. The
    // earlier rule ("the X button never pops a panel", to preserve the Generate button's cold
    // pre-select) predates the permanent panel and left a selected tile beside a ProjectActionsView.
    //
    // The assert below is NOT sufficient on its own for that tab-click return: it reads the
    // selection the Timeline had, and the workspace's own shot only arrives afterwards, from
    // the unmount cleanup. boardPanelAssertPending (armed just above) is what carries the
    // arrival across to that later write; this assert still owns the plain Timeline→Board case,
    // where nothing runs after it.
    //
    // TUTORIAL EXCLUSION: during the guided tour the render-step latch (SceneBoardExpanded)
    // selects the glow target programmatically precisely so the panel stays SHUT — an open panel
    // narrows the board and covers the tile the rail is pointing at. Step 4's own Enhance no
    // longer leaves the board (2026-08-21: it opens BoardEnhanceDialog in place), but AI Studio
    // still hops board→workspace→board mid-tour, and without this the return would defeat the
    // latch.
    if (boardArrival) {
      const ps = useProjectStore.getState();
      if (ps.selectedShotId != null && !ps.userInitiatedShotSelection) {
        useProjectStore.setState({ userInitiatedShotSelection: true });
      }
    }
  },
  consumeBoardPanelAssert: () => {
    const armed = get().boardPanelAssertPending;
    // Read-and-CLEAR, so a StrictMode double-cleanup (or any second caller) sees false and the
    // arm can only ever pop one panel. Skip the write when it is already false — this runs from
    // an unmount cleanup, and a no-op set() there would notify every subscriber for nothing.
    if (armed) set({ boardPanelAssertPending: false });
    return armed;
  },
  consumeCreateAddAsNewShot: () => {
    const armed = get().createAddAsNewShotPending;
    // Read-and-CLEAR, exactly as above, and NOT optional: the route write that arms this only
    // self-clears on the next route CHANGE, and several ways into the workspace (the ShotBar's
    // quick generate, ViewerOverlay) call openAiWorkspace with no route write at all. Without the
    // clear, one deferred hand-off the user then abandoned would tick the box on the next
    // unrelated open. Skip the write when already false — this runs from a mount-time read and a
    // no-op set() would notify every subscriber for nothing.
    //
    // STRICTMODE IS FINE (corrected 2026-09-01 against the installed react-dom 19.2.4; this comment
    // used to claim the opposite). Consumed from a useState LAZY INITIALIZER, and React does invoke
    // that initializer twice in a StrictMode dev build — but `mountStateImpl` KEEPS THE FIRST
    // RESULT and throws the second away (the re-invoke is side-effect detection only), and
    // StrictMode's second RENDER goes through renderWithHooksAgain on the rerender dispatcher,
    // which reuses the mounted hook state and never re-runs initializers at all. So the latch is
    // consumed exactly once and the seed survives `npm run dev`.
    // Fallback argument if that ever changes: an unticked box is the SAFE direction — it degrades
    // to the shipped default and can only ever fail to create a shot, never create one unasked.
    if (armed) set({ createAddAsNewShotPending: false });
    return armed;
  },
  consumeAiWorkspacePromptHandoff: () => {
    const handoff = get().aiWorkspacePromptHandoff;
    // Read-and-CLEAR, for the same reasons consumeCreateAddAsNewShot spells out above — including
    // the same StrictMode answer: this is read from a useState lazy initializer, React's dev-only
    // double-invoke discards the second call's result, and the StrictMode second render reuses hook
    // state instead of re-running initializers, so the hand-off is consumed exactly once and DOES
    // work on the dev server. Fallback argument if that ever changes: an empty box is the SAFE
    // direction — it degrades to the shipped "the typed prompt does not carry over" behaviour and
    // can never put stale words in front of a spend button. Skip the write when there is nothing
    // armed: a no-op set() would notify every subscriber for nothing.
    if (handoff) set({ aiWorkspacePromptHandoff: null });
    return handoff;
  },
  enterAiWorkspaceFromBoard: (shotId, tab = 'stylegrade', opts) => {
    // Board → AI Workspace: the workspace renders inside the Timeline view, so switch
    // to the timeline tab first; openAiWorkspace does not touch workspaceRoute/return flag.
    //
    // The "Add as new shot" seed and the prompt hand-off are written HERE, in the route set(), and
    // unconditionally — a caller that omits opts writes false/null, so a stale arm from an earlier
    // hand-off can never be inherited by this one. See createAddAsNewShotPending's declaration.
    set({
      workspaceRoute: 'timeline',
      aiWorkspaceReturnToBoard: true,
      createAddAsNewShotPending: opts?.createAddAsNewShot === true,
      aiWorkspacePromptHandoff: opts?.promptHandoff ?? null,
    });
    get().openAiWorkspace('enhance', undefined, shotId);
    // The landing tab became 'create' on 2026-08-25, and openAiWorkspace does NOT reset it (only
    // closeAiWorkspace's finalize does, i.e. on the PREVIOUS close) — so every arrival here names
    // the tab rather than inheriting whatever was left behind. The 'stylegrade' default is the
    // Enhance intent this entry point has always carried (AI Studio on a specific shot), same
    // reason the ShotBar/ShotLane/keyboard entry points name theirs; a caller that arrives with a
    // Create intent passes 'create' instead. Nothing here touches boardPanelAssertPending: that
    // latch means "arriving AT the Board" and this is the departure — the board's Generate button
    // pre-selects the shot COLD on purpose, and the matching arrival is armed on the way back out
    // (closeAiWorkspace's returnToBoard finalize, 20260902).
    set({ aiWorkspaceTab: tab });
  },
  openBoardEnhanceDialog: (shotId, deferredSceneId = null, initialArm = 'create') => set({
    boardEnhanceShotId: shotId,
    // Defaults to null, so every shipped call site (the tile chip, the panel row) keeps opening a
    // write-to-this-shot dialog by omitting the argument. See the field's declaration for what a
    // non-null value means — and note it is written HERE rather than left over from a previous
    // open: a deferred open followed by a plain one must not inherit the scene id.
    boardEnhanceDeferredSceneId: deferredSceneId,
    // Same reasoning as deferredSceneId above: written HERE, every open, so a prior Enhance-entry
    // open can never leak its arm into a plain one. The tile's AI chip is the only caller that
    // passes 'enhance'; every other entry point omits the argument and keeps the Create-first open.
    boardEnhanceInitialArm: initialArm,
    // Image on every open, per the owner's spec. aiWorkspaceTask is shared with the Timeline
    // workspace on purpose — the board already forced it there via openAiWorkspace('enhance', …).
    aiWorkspaceTask: 'enhance',
    // The same conflicting-overlay sweep openAiWorkspace does above. NOT workspaceRoute /
    // aiWorkspaceOpen / aiWorkspaceReturnToBoard: this dialog opens IN PLACE over the board,
    // and writing any of those three is exactly the route hop it exists to remove.
    quickConfirmMode: null,
    ttsPopoverOpen: false,
    audioWorkspaceOpen: false,
  }),
  closeBoardEnhanceDialog: () => set({
    boardEnhanceShotId: null,
    boardEnhanceDeferredSceneId: null,
    boardEnhanceInitialArm: 'create',
  }),
  // The chooser sweeps the SAME three conflicting overlays openBoardEnhanceDialog does, for the
  // same reason: it is a modal over the board and one of its two rows opens that dialog directly,
  // so the sweep has to have happened by the time the row is tapped rather than a beat later.
  // 20261008 (owner) — `afterShotId` is written on EVERY open (null when omitted), so a plain add
  // can never inherit a stale insert anchor, exactly as it never inherits a replace target.
  openAddShotChooser: (sceneId, opts) => set({
    addShotChooserSceneId: sceneId,
    addShotChooserAfterShotId: opts?.afterShotId ?? null,
    addShotChooserReplaceShotId: null,
    quickConfirmMode: null,
    ttsPopoverOpen: false,
    audioWorkspaceOpen: false,
  }),
  // Same sweep as the add-mode open: the Sketch door opens BoardEnhanceDialog directly.
  openReplaceShotChooser: (sceneId, shotId) => set({
    addShotChooserSceneId: sceneId,
    addShotChooserAfterShotId: null,
    addShotChooserReplaceShotId: shotId,
    quickConfirmMode: null,
    ttsPopoverOpen: false,
    audioWorkspaceOpen: false,
  }),
  closeAddShotChooser: () => set({
    addShotChooserSceneId: null, addShotChooserAfterShotId: null, addShotChooserReplaceShotId: null,
  }),
  setImagePickPending: (pending) => set({ imagePickPending: pending }),
  setPhotosDeniedDialogOpen: (open) => set({ photosDeniedDialogOpen: open }),
  setAiWorkspaceTask: (task) => set({ aiWorkspaceTask: task }),
  setAiWorkspaceTab: (tab: AiWorkspaceTab) => set({ aiWorkspaceTab: tab }),
  setAnimaticRegenPending: (pending) => set({ animaticRegenPending: pending }),
  clearAnimaticRegenPending: () => set({ animaticRegenPending: null }),
  showAiError: (title, message) => set({ aiErrorDialog: { title, message } }),
  closeAiError: () => set({ aiErrorDialog: null }),

  openAudioWorkspace: (mode) => {
    if (get().aiWorkspaceOpen && useAiStore.getState().isAnyGenerating()) {
      const resolvedMode = mode ?? 'tts';
      set({
        aiClosePendingAction: () => set({
          audioWorkspaceOpen: true,
          audioWorkspaceMode: resolvedMode,
          aiWorkspaceOpen: false,
          aiWorkspaceInitialPair: null,
          // Switching to the Audio workspace closes the AI workspace by a path other than
          // closeAiWorkspace, so clear the board-return arm here too (else it leaks to a
          // later AI-workspace close and mis-routes it to the board).
          aiWorkspaceReturnToBoard: false,
          glowTutorialStep: null,
          aiClosePendingAction: null,
          quickConfirmMode: null,
          ttsPopoverOpen: false,
        }),
      });
    } else {
      set({
        audioWorkspaceOpen: true,
        audioWorkspaceMode: mode ?? 'tts',
        aiWorkspaceOpen: false,
        aiWorkspaceInitialPair: null,
        // Clear the board-return arm — see the deferred branch above.
        aiWorkspaceReturnToBoard: false,
        glowTutorialStep: null,
        quickConfirmMode: null,
        ttsPopoverOpen: false,
      });
    }
  },
  closeAudioWorkspace: () => set({ audioWorkspaceOpen: false }),
  closeOpenWorkspace: () => {
    // Delegate to the same actions the workspace headers' X buttons call — never inline the
    // setState. closeAiWorkspace owns BOTH the return-to-board finalize (when the workspace
    // was opened from the board's Enhance button) and the generation-in-progress deferral,
    // and inlining here would strand workspaceRoute / aiWorkspaceReturnToBoard. Same contract
    // setWorkspaceRoute follows when leaving the Timeline tab.
    // Check both flags independently rather than as an if/else: they are mutually exclusive
    // by construction today (each open action clears the other), but nothing enforces it, and
    // an else-branch would silently leave the second workspace open if that ever changed.
    // Each branch re-reads through get() rather than sharing one pre-close snapshot, so the
    // audio test sees whatever closeAiWorkspace actually left behind. A snapshot would only be
    // sound while the AI close never touches audioWorkspaceOpen — an assumption worth removing
    // rather than documenting, since it is exactly what the two-if pin exists to survive.
    let handled = false;
    if (get().aiWorkspaceOpen) {
      get().closeAiWorkspace();
      handled = true;
    }
    if (get().audioWorkspaceOpen) {
      get().closeAudioWorkspace();
      handled = true;
    }
    // NOTE for callers: `true` means "a workspace was showing and its close action ran", NOT
    // "the workspace is now closed". With a generation in flight closeAiWorkspace parks the
    // finalize in aiClosePendingAction and shows its confirmation dialog instead. Returning
    // early on `true` is what keeps back from closing the panel out from under that dialog.
    return handled;
  },
  setAudioWorkspaceMode: (mode) => set({ audioWorkspaceMode: mode }),
  setAudioWorkspaceTargetLane: (lane) => set({ audioWorkspaceTargetLane: lane }),
  setScratchType: (type) => {
    set({ scratchType: type });
  },
  setScratchScope: (scope) => set({ scratchScope: scope }),
  setScratchScriptCoverage: (enabled) => set({ scratchScriptCoverage: enabled }),
  setScreenplayBlocking: (enabled) => set({ screenplayBlocking: enabled }),
  setSceneShotSettings: (enabled) => set({ sceneShotSettings: enabled }),

  openDoodleDialog: (shotId) => set({ doodleDialogOpen: true, doodleDialogShotId: shotId }),
  closeDoodleDialog: () => set({ doodleDialogOpen: false, doodleDialogShotId: null }),

  openPostDrawPrompt: (shotId, paths, screenplayPage, sceneId) => set({
    postDrawPromptOpen: true,
    postDrawPromptShotId: shotId,
    postDrawPromptPaths: paths,
    postDrawPromptScreenplayPage: screenplayPage ?? null,
    postDrawPromptSceneId: sceneId ?? null,
  }),
  closePostDrawPrompt: () => set({
    postDrawPromptOpen: false,
    postDrawPromptShotId: null,
    postDrawPromptPaths: null,
    postDrawPromptScreenplayPage: null,
    postDrawPromptSceneId: null,
  }),

  setExportHubOpen: (open) => set({ exportHubOpen: open }),
  openExportHub: (initialTab) => set({ exportHubOpen: true, exportHubInitialTab: initialTab }),
  setShareDialogOpen: (open) => set({ shareDialogOpen: open }),

  setScriptAnnotationMode: (on) => set({ scriptAnnotationMode: on, ...(!on ? { scriptAnnotationPage: null } : {}) }),
  setScriptAnnotationPage: (page) => set({ scriptAnnotationPage: page }),
  toggleScriptAnnotationMode: () => set(s => ({
    scriptAnnotationMode: !s.scriptAnnotationMode,
    ...(!s.scriptAnnotationMode ? {} : { scriptAnnotationPage: null }),
  })),

  teaseOverlay: null,
  setTeaseOverlay: (kind) => set({ teaseOverlay: kind }),

  orientStep: null,
  setOrientStep: (step) => set({ orientStep: step }),

  scriptOrientStep: null,
  setScriptOrientStep: (step) => set({ scriptOrientStep: step }),

  glowTutorialStep: null,
  setGlowTutorialStep: (step) => set({ glowTutorialStep: step }),

  remoteCommentPing: 0,
  pingRemoteComment: () => set(s => ({ remoteCommentPing: s.remoteCommentPing + 1 })),

  uiScaleLevel: 3, // Large = current sizes (default)
  setUIScaleLevel: (level) => set({ uiScaleLevel: Math.max(0, Math.min(6, level)) }),

  sceneBoardViewMode: 'expanded',
  setSceneBoardViewMode: (mode) => set({ sceneBoardViewMode: mode }),
  toggleSceneBoardViewMode: () => set(s => ({ sceneBoardViewMode: s.sceneBoardViewMode === 'compact' ? 'expanded' : 'compact' })),

  sceneBoardAnchorSceneId: null,
  setSceneBoardAnchor: (sceneId) => set({ sceneBoardAnchorSceneId: sceneId }),
  compactScroll: null,
  setCompactScroll: (v) => set({ compactScroll: v }),
  expandedScroll: null,
  setExpandedScroll: (v) => set({ expandedScroll: v }),
  sceneBoardHasMounted: false,

  timelineAnchor: null,
  setTimelineAnchor: (sceneId) => set({ timelineAnchor: sceneId }),

  expandedSceneId: null,
  setExpandedScene: (id) => set({ expandedSceneId: id }),
  toggleExpandedScene: (id) =>
    set((s) => ({ expandedSceneId: s.expandedSceneId === id ? null : id })),
  boardSelectMode: false,
  boardSelectedShotIds: [],
  enterBoardSelectMode: (seedShotId) =>
    set({ boardSelectMode: true, boardSelectedShotIds: seedShotId != null ? [seedShotId] : [] }),
  exitBoardSelectMode: () => set({ boardSelectMode: false, boardSelectedShotIds: [] }),
  toggleBoardShotSelected: (id) => set((s) => ({
    boardSelectedShotIds: s.boardSelectedShotIds.includes(id)
      ? s.boardSelectedShotIds.filter((v) => v !== id)
      : [...s.boardSelectedShotIds, id],
  })),
  pruneBoardSelection: (existingIds) => {
    const current = get().boardSelectedShotIds;
    const next = current.filter((id) => existingIds.has(id));
    // Identity check, not a length check for its own sake: this runs from an effect keyed on the
    // project, so an unconditional set() would notify every subscriber on every repush.
    if (next.length !== current.length) set({ boardSelectedShotIds: next });
  },
  shotPanelView: 'main',
  setShotPanelView: (v) => set({ shotPanelView: v }),
  screenplayExpandedSceneId: null,
  screenplayExpandedCharacter: null,
  screenplayExpandedLocation: null,
  screenplayFilterSceneId: null,
  screenplaySearchQuery: '',
  screenplayBrowseTab: 'screenplay',
  screenplayRevealCharacter: null,
  setScreenplayRevealCharacter: (name) => set({ screenplayRevealCharacter: name }),
  consumeScreenplayRevealCharacter: () => {
    const name = get().screenplayRevealCharacter;
    // Skip the write when it is already null: the consumer's effect is keyed on the rendered
    // roster as well as on this field, so an unconditional set() would notify every subscriber on
    // every repush.
    if (name !== null) set({ screenplayRevealCharacter: null });
    return name;
  },
  setScreenplayExpandedSceneId: (id) => set({ screenplayExpandedSceneId: id }),
  setScreenplayExpandedCharacter: (name) => set({ screenplayExpandedCharacter: name }),
  setScreenplayExpandedLocation: (key) => set({ screenplayExpandedLocation: key }),
  setScreenplayFilterSceneId: (id) => set({ screenplayFilterSceneId: id }),
  setScreenplaySearchQuery: (query) => set({ screenplaySearchQuery: query }),
  setScreenplayBrowseTab: (tab) => set({ screenplayBrowseTab: tab }),
  toggleScreenplayExpandedScene: (id) =>
    set((s) => ({ screenplayExpandedSceneId: s.screenplayExpandedSceneId === id ? null : id })),
  toggleScreenplayExpandedCharacter: (name) =>
    set((s) => ({ screenplayExpandedCharacter: s.screenplayExpandedCharacter === name ? null : name })),
  toggleScreenplayExpandedLocation: (key) =>
    set((s) => ({ screenplayExpandedLocation: s.screenplayExpandedLocation === key ? null : key })),
  canvasEntryBusy: false,
  setCanvasEntryBusy: (busy) => set({ canvasEntryBusy: busy }),
  sceneMutationBusy: null,
  setSceneMutationBusy: (op) => set({ sceneMutationBusy: op }),
  shellHomeShed: false,
  setShellHomeShed: (shed) => set({ shellHomeShed: shed }),

  coverageOpenRequestSceneId: null,
  consumeCoverageOpenRequest: () => {
    const sceneId = get().coverageOpenRequestSceneId;
    // Read-and-CLEAR, the consumeCreateAddAsNewShot idiom: one arm opens one dialog, so a second
    // caller (a StrictMode double-run, an effect re-firing on a repush) must find nothing. Skip the
    // write when it is already null — the consumer's effect is keyed on the PROJECT as well as on
    // this field, so a no-op set() would notify every subscriber on every repush.
    if (sceneId != null) set({ coverageOpenRequestSceneId: null });
    return sceneId;
  },
  requestCoverageForScene: (sceneId) => {
    // ROUTE FIRST, LATCH SECOND, in two set()s. setWorkspaceRoute's trailing set() clears both
    // checklist latches unconditionally — which is what makes an unconsumed arm safe — so an arm
    // merged into the route write would be wiped by the very write meant to carry it.
    get().setWorkspaceRoute('board');
    // ONE set for the whole arrival state: expanded mode, the scene disclosed, the scroll anchor on
    // it, and the overview asked for. Split writes would re-render the board once per field and
    // leave the anchor briefly disagreeing with the disclosure.
    set({
      sceneBoardViewMode: 'expanded',
      expandedSceneId: sceneId,
      sceneBoardAnchorSceneId: sceneId,
      coverageOpenRequestSceneId: sceneId,
    });
  },
  scriptViewChangesRequestPending: false,
  consumeScriptViewChangesRequest: () => {
    const armed = get().scriptViewChangesRequestPending;
    // Read-and-CLEAR on the same terms as consumeCoverageOpenRequest above, no-op write included:
    // the consumer is a SUBSCRIBED effect (the user may already be on the Script tab), so it runs
    // on every change to the field it watches.
    if (armed) set({ scriptViewChangesRequestPending: false });
    return armed;
  },
  requestScriptViewChanges: () => {
    // Route first, latch second — see requestCoverageForScene above for why they cannot merge.
    get().setWorkspaceRoute('screenplay');
    set({ scriptViewChangesRequestPending: true });
  },
}));

/** Map scaling level (0-6) to a multiplier. Level 3 (Large) = 1.0 = current sizes. */
const UI_SCALE_FACTORS = [0.70, 0.80, 0.90, 1.00, 1.10, 1.20, 1.30];
export function getUIScaleFactor(level: number): number {
  return UI_SCALE_FACTORS[Math.max(0, Math.min(6, level))] ?? 1.0;
}
